/**
 * The bindings store: the rules of `model.ts` plus persistence and change notices. It needs a
 * `getItem` / `setItem` pair and nothing else, so tests hand it a plain object; a missing,
 * blocked or corrupt store just means defaults.
 */

import { ACTIONS, type ActionId, type BindingsPort, type Scheme } from './actions';
import { defaultTable } from './defaults';
import { type BindingsData, lookupTable, rebindIn, sanitise } from './model';

export const BINDINGS_KEY = 'nightdrop.bindings.v1';

/** The slice of `localStorage` the store needs. */
export interface KeyValue {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** What the input layer reads, on top of the port the menus use. */
export interface Bindings extends BindingsPort {
  /** The action a key is bound to in the active scheme, or null. */
  lookup(code: string): ActionId | null;
  /** The active scheme's keys for one action. */
  keysFor(action: ActionId): readonly string[];
  /** Calls `listener` after every change; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

/** `window.localStorage` where there is one and the browser lets the page have it. */
function browserStorage(): KeyValue | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** A bindings store over `storage`; pass null for one that lives only as long as the page. */
export function createBindings(storage: KeyValue | null = browserStorage()): Bindings {
  let raw: unknown = null;
  try {
    const text = storage?.getItem(BINDINGS_KEY) ?? null;
    if (text !== null) raw = JSON.parse(text);
  } catch {
    raw = null;
  }
  const data: BindingsData = sanitise(raw);
  let lookup = lookupTable(data.tables[data.scheme]);
  const listeners = new Set<() => void>();

  const changed = (): void => {
    lookup = lookupTable(data.tables[data.scheme]);
    try {
      storage?.setItem(BINDINGS_KEY, JSON.stringify(data));
    } catch {
      // Private browsing or a full store: the bindings still hold for this session.
    }
    for (const listener of listeners) listener();
  };

  return {
    scheme: () => data.scheme,
    setScheme(s: Scheme) {
      if (s !== 'modern' && s !== 'classic') return;
      if (s === data.scheme) return;
      data.scheme = s;
      changed();
    },
    actions: () => {
      const table = data.tables[data.scheme];
      return ACTIONS.map(({ id, label }) => ({ id, label, keys: [...table[id]] }));
    },
    rebind(actionId, slot, code) {
      const problem = rebindIn(data.tables[data.scheme], actionId, slot, code);
      if (problem === null) changed();
      return problem;
    },
    reset() {
      data.tables[data.scheme] = defaultTable(data.scheme);
      changed();
    },
    lookup: (code) => lookup.get(code) ?? null,
    keysFor: (action) => data.tables[data.scheme][action],
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

let shared: Bindings | null = null;

/** The one store the menus, the input layer and the HUD share, backed by `localStorage`. */
export function getBindings(): Bindings {
  if (shared === null) shared = createBindings();
  return shared;
}
